import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw, Database, Package, Tag, Factory, Truck, FileDown, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 100000;

const USNMasterExport = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [downloadProgress, setDownloadProgress] = useState({});

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usn-export/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load USN statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const convertToCSV = (data, headers) => {
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(h => {
          const val = row[h] ?? '';
          const strVal = String(val);
          if (strVal.includes(',') || strVal.includes('"') || strVal.includes('\n')) {
            return `"${strVal.replace(/"/g, '""')}"`;
          }
          return strVal;
        }).join(',')
      )
    ].join('\n');
    return csvContent;
  };

  const downloadFile = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadCSV = async (endpoint, baseFilename, key, totalRecords) => {
    const totalPages = Math.ceil(totalRecords / PAGE_SIZE);
    
    if (totalPages <= 0) {
      toast.warning('No data to export');
      return;
    }

    setDownloading(prev => ({ ...prev, [key]: true }));
    setDownloadProgress(prev => ({ ...prev, [key]: { current: 0, total: totalPages } }));

    try {
      const token = localStorage.getItem('token');
      
      for (let page = 1; page <= totalPages; page++) {
        setDownloadProgress(prev => ({ 
          ...prev, 
          [key]: { current: page, total: totalPages } 
        }));

        const response = await fetch(
          `${API}/api/admin/usn-export/${endpoint}?page=${page}&page_size=${PAGE_SIZE}`, 
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (!response.ok) throw new Error(`Failed to fetch page ${page}`);
        const result = await response.json();
        
        if (!result.data || result.data.length === 0) {
          if (page === 1) {
            toast.warning('No data to export');
            return;
          }
          break;
        }
        
        // Convert to CSV and download
        const headers = Object.keys(result.data[0]);
        const csvContent = convertToCSV(result.data, headers);
        
        // Generate filename with page number if multiple pages
        const filename = totalPages > 1 
          ? `${baseFilename}_part${page}_of_${totalPages}.csv`
          : `${baseFilename}.csv`;
        
        downloadFile(csvContent, filename);
        
        // Small delay between downloads to prevent browser issues
        if (page < totalPages) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      toast.success(`Downloaded ${totalRecords.toLocaleString()} records in ${totalPages} file(s)`);
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download CSV');
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
      setDownloadProgress(prev => ({ ...prev, [key]: null }));
    }
  };

  const exportOptions = [
    {
      key: 'fob_section',
      title: 'FOB Section USNs',
      description: 'USNs assigned via Sort No → USN Tagging (Auto Allocation)',
      icon: Package,
      color: 'bg-blue-500',
      endpoint: 'fob-section',
      statsKey: 'fob_section',
      columns: ['USN Code', 'Sort No', 'FG Barcode', 'Document', 'Status']
    },
    {
      key: 'goods_transfer',
      title: 'Goods Transfer USNs',
      description: 'USNs from Packing operation (Manual Allocation)',
      icon: Truck,
      color: 'bg-cyan-500',
      endpoint: 'goods-transfer',
      statsKey: 'goods_transfer',
      columns: ['USN Code', 'Bundle', 'FG Name', 'Sort No', 'Color']
    },
    {
      key: 'label_assignments',
      title: 'Main Label Printing USNs',
      description: 'USNs assigned during label printing (Auto Allocation)',
      icon: Tag,
      color: 'bg-emerald-500',
      endpoint: 'label-assignments',
      statsKey: 'label_assignments',
      columns: ['USN Code', 'Item Barcode', 'Design Name', 'Label Type']
    },
    {
      key: 'greige_section',
      title: 'Greige Section USNs',
      description: 'USNs from Greige Inspection (Manual Allocation)',
      icon: Factory,
      color: 'bg-amber-500',
      endpoint: 'greige-section',
      statsKey: 'greige_section',
      columns: ['USN Code', 'Bundle', 'Sort No', 'Status', 'Damage']
    }
  ];

  const getFileCount = (totalRecords) => {
    if (!totalRecords) return 0;
    return Math.ceil(totalRecords / PAGE_SIZE);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              className="text-white hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Database className="w-7 h-7" />
              <div>
                <h1 className="text-xl font-bold">USN Master Export</h1>
                <p className="text-xs text-gray-300">Download USN data from all modules</p>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchStats}
            disabled={loading}
            className="text-white hover:bg-white/10"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <h3 className="font-semibold text-blue-800 mb-2">USN Allocation Points</h3>
          <p className="text-sm text-blue-700">
            This tool exports USN data from 4 different allocation points in the system.
            <strong className="block mt-2">Large datasets are automatically split into files of 100,000 records each.</strong>
          </p>
          <ul className="text-sm text-blue-600 mt-2 space-y-1 list-disc list-inside">
            <li><strong>FOB Section</strong> - Auto allocation via Sort No → USN Tagging</li>
            <li><strong>Goods Transfer</strong> - Manual allocation during Packing</li>
            <li><strong>Main Label Printing</strong> - Auto allocation during label generation</li>
            <li><strong>Greige Section</strong> - Manual allocation during Inspection</li>
          </ul>
        </div>

        {/* Export Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {exportOptions.map((option) => {
            const Icon = option.icon;
            const statData = stats?.[option.statsKey];
            const totalRecords = statData?.total || 0;
            const fileCount = getFileCount(totalRecords);
            const progress = downloadProgress[option.key];
            
            return (
              <div
                key={option.key}
                className="bg-white rounded-xl border border-[#E5DFD0] shadow-sm overflow-hidden"
                data-testid={`export-card-${option.key}`}
              >
                {/* Card Header */}
                <div className={`${option.color} p-4 flex items-center gap-3`}>
                  <Icon className="w-8 h-8 text-white" />
                  <div>
                    <h3 className="text-lg font-bold text-white">{option.title}</h3>
                    <p className="text-sm text-white/80">{option.description}</p>
                  </div>
                </div>
                
                {/* Card Body */}
                <div className="p-4">
                  {/* Stats */}
                  {loading ? (
                    <div className="flex items-center gap-2 text-gray-500 mb-4">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Loading stats...</span>
                    </div>
                  ) : statData ? (
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-[#1A1F2E]">
                          {totalRecords.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">Total Records</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-[#2D5F4F]">
                          {(statData.assigned || statData.with_fg_assignment || statData.with_sort_no || totalRecords).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {option.key === 'fob_section' ? 'Assigned' : 
                           option.key === 'goods_transfer' ? 'With FG' :
                           option.key === 'greige_section' ? 'With Sort No' : 'Records'}
                        </p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-blue-600">
                          {fileCount}
                        </p>
                        <p className="text-xs text-gray-500">
                          {fileCount === 1 ? 'File' : 'Files'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 mb-4">No data available</div>
                  )}
                  
                  {/* Columns Preview */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Export Columns:</p>
                    <div className="flex flex-wrap gap-1">
                      {option.columns.map((col, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-gray-100 text-xs text-gray-600 rounded"
                        >
                          {col}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* File count info */}
                  {fileCount > 1 && !downloading[option.key] && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-700">
                        <FileDown className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Will download {fileCount} files ({PAGE_SIZE.toLocaleString()} records each)
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Progress indicator */}
                  {progress && (
                    <div className="mb-4 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          Downloading file {progress.current} of {progress.total}
                        </span>
                        <span className="font-medium text-blue-600">
                          {Math.round((progress.current / progress.total) * 100)}%
                        </span>
                      </div>
                      <Progress 
                        value={(progress.current / progress.total) * 100} 
                        className="h-2"
                      />
                    </div>
                  )}
                  
                  {/* Download Button */}
                  <Button
                    onClick={() => downloadCSV(
                      option.endpoint, 
                      `${option.key}_export_${new Date().toISOString().slice(0,10)}`, 
                      option.key,
                      totalRecords
                    )}
                    disabled={downloading[option.key] || totalRecords === 0}
                    className="w-full"
                    data-testid={`download-${option.key}`}
                  >
                    {downloading[option.key] ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Downloading {progress?.current || 1} of {progress?.total || fileCount}...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download {fileCount > 1 ? `${fileCount} CSV Files` : 'CSV'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Collection Reference */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-700 mb-3">Database Collection Reference</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-gray-600">FOB Section:</p>
              <code className="text-xs bg-gray-200 px-2 py-1 rounded">usns (section="FOB")</code>
            </div>
            <div>
              <p className="font-medium text-gray-600">Goods Transfer:</p>
              <code className="text-xs bg-gray-200 px-2 py-1 rounded">goods_transfer_packed_usns</code>
            </div>
            <div>
              <p className="font-medium text-gray-600">Main Labels:</p>
              <code className="text-xs bg-gray-200 px-2 py-1 rounded">label_usn_assignments</code>
            </div>
            <div>
              <p className="font-medium text-gray-600">Greige Section:</p>
              <code className="text-xs bg-gray-200 px-2 py-1 rounded">greige_usns</code>
            </div>
          </div>
        </div>

        {/* Note about file downloads */}
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-800">Download Information</h4>
              <p className="text-sm text-green-700 mt-1">
                Each file contains up to {PAGE_SIZE.toLocaleString()} records. For large datasets, 
                multiple files will be downloaded sequentially. Files are named with part numbers 
                (e.g., <code className="bg-green-100 px-1 rounded">_part1_of_3.csv</code>) for easy identification.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default USNMasterExport;
